import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Default hotkey configuration for new characters
const defaultHotkeys = [
  [
    { key: 'F1', name: "Attack", cmd: "/a" },
    { key: 'F2', name: "Stop Attack", cmd: "/stop" },
    { key: 'F3', name: "Edit Hotkeys", cmd: "/hotkey" },
    { key: 'F4', name: "Help", cmd: "/help" },
    { key: 'F5', name: "Quests", cmd: "/quests" },
    { key: 'F6', name: "", cmd: "" },
    { key: 'F7', name: "", cmd: "" },
    { key: 'F8', name: "", cmd: "" },
    { key: 'F9', name: "", cmd: "" },
  ],
  [
    { key: 'F1', name: "", cmd: "" },
    { key: 'F2', name: "", cmd: "" },
    { key: 'F3', name: "", cmd: "" },
    { key: 'F4', name: "", cmd: "" },
    { key: 'F5', name: "", cmd: "" },
    { key: 'F6', name: "", cmd: "" },
    { key: 'F7', name: "", cmd: "" },
    { key: 'F8', name: "", cmd: "" },
    { key: 'F9', name: "", cmd: "" },
  ],
  [
    { key: 'F1', name: "", cmd: "" },
    { key: 'F2', name: "", cmd: "" },
    { key: 'F3', name: "", cmd: "" },
    { key: 'F4', name: "", cmd: "" },
    { key: 'F5', name: "", cmd: "" },
    { key: 'F6', name: "", cmd: "" },
    { key: 'F7', name: "", cmd: "" },
    { key: 'F8', name: "", cmd: "" },
    { key: 'F9', name: "", cmd: "" },
  ],
  [
    { key: 'F1', name: "", cmd: "" },
    { key: 'F2', name: "", cmd: "" },
    { key: 'F3', name: "", cmd: "" },
    { key: 'F4', name: "", cmd: "" },
    { key: 'F5', name: "", cmd: "" },
    { key: 'F6', name: "", cmd: "" },
    { key: 'F7', name: "", cmd: "" },
    { key: 'F8', name: "", cmd: "" },
    { key: 'F9', name: "", cmd: "" },
  ],
];

// Default mouse options
const defaultMouseOptions = [
  { key: 'M1', name: "Attack", cmd: "/a {target}" },
  { key: 'M2', name: "Examine", cmd: "/examine {target}" },
  { key: 'M3', name: "Chat", cmd: "/t {target} hi" },
  { key: 'M4', name: "", cmd: "" },
  { key: 'M5', name: "", cmd: "" },
];

// Default ctrl+click options
const defaultCtrlClickOptions = {
  leftClick: { name: "Attack", cmd: "/a {target}" },
  rightClick: { name: "Examine", cmd: "/examine {target}" }
};

// Deep clone helper function
const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

// Create a store factory function that takes a character ID
export const createHotkeyStore = (characterId) => {
  // Return a new store instance for this character
  return create(
    persist(
      (set) => ({
        actions: deepClone(defaultHotkeys), // Deep copy the default hotkeys
        mouseOptions: deepClone(defaultMouseOptions), // Deep copy the default mouse options
        ctrlClickOptions: deepClone(defaultCtrlClickOptions), // Deep copy the default ctrl+click options
        setActions: (actions) => set({ actions }),
        setMouseOptions: (mouseOptions) => set({ mouseOptions }),
        setCtrlClickOptions: (ctrlClickOptions) => set({ ctrlClickOptions }),
      }),
      {
        name: `hotkeys-${characterId}`, // Use character ID in storage key
        getStorage: () => localStorage,
      }
    )
  );
};

// Store registry to keep track of created stores
const hotkeyStores = {};

// Function to get or create a store for a character
export const getHotkeyStore = (characterId) => {
  if (!characterId) {
    console.error("Character ID is required to get hotkey store");
    return null;
  }
  
  // If we don't have a store for this character yet, create one
  if (!hotkeyStores[characterId]) {
    hotkeyStores[characterId] = createHotkeyStore(characterId);
  }
  
  return hotkeyStores[characterId];
};

// Function to initialize a character's hotkeys with defaults if they don't exist yet
export const initializeHotkeys = (characterId) => {
  if (!characterId) return null;
  
  const storageKey = `hotkeys-${characterId}`;
  const existingData = localStorage.getItem(storageKey);
  
  // If no data exists for this character, create default hotkeys
  if (!existingData) {
    const store = getHotkeyStore(characterId);
    if (store) {
      store.getState().setActions(deepClone(defaultHotkeys));
      store.getState().setMouseOptions(deepClone(defaultMouseOptions));
      store.getState().setCtrlClickOptions(deepClone(defaultCtrlClickOptions));
      return store;
    }
  }
  
  return getHotkeyStore(characterId);
};

// Helper function to process mouse option commands
export const processMouseCommand = (cmd, targetIndex, targetName) => {
  if (!cmd) return "";
  
  // Replace {target} with the target index or name
  return cmd.replace(/{target}/g, targetIndex !== undefined ? targetIndex : targetName || "");
}; 